import { useLayoutEffect, useState, type RefObject } from 'react';

export interface FloatingRect {
  top: number;
  left: number;
  width: number;
  /**
   * 実際に配置した向き。`bottom` = trigger の下、`top` = 上へ反転した状態。
   * 呼び出し側がアニメーション方向や矢印の向きを変えたい時に使う。
   */
  placement: 'bottom' | 'top';
  /**
   * 配置側に確保できた高さ (px)。popover 自身の `max-height` に渡すと、上下どちらにも
   * 収まりきらない時に画面外へはみ出さず内部スクロールに切り替わる。
   */
  maxHeight: number;
}

/** trigger と popover の間隔 (px)。 @graph-connects none */
const GAP = 4;
/** 画面端との最小マージン (px)。 @graph-connects none */
const VIEWPORT_MARGIN = 8;
/** 反転先も潰れている時に確保する下限高さ (px)。最低 2〜3 行は見える。 @graph-connects none */
const MIN_HEIGHT = 96;

/**
 * trigger 要素の bounding rect から popover の fixed 座標を計算する。scroll/resize で再計算。
 * Picker / MultiPicker / 各アプリのドメイン Picker が共有する。
 *
 * `floatingRef` を渡すと popover の実高さを測り、下に収まらない場合は上へ反転する。
 * 反転しないと、画面下端に近い trigger の popover が viewport の外へ出る。popover は
 * `position: fixed` なのでスクロールしても追いつけず、末尾の候補が**永久にクリックできない**
 * （テーブル最下行のセレクトで「一番下の選択肢が押せない」形で出る）。上下どちらにも
 * 収まらない時は広い側に置き、`maxHeight` を返して popover 内スクロールに切り替えさせる。
 *
 * `floatingRef` 未指定時は実高さが測れないため従来どおり常に下へ置く（後方互換）。
 *
 * @graph-stack web-design-system
 * @graph-domain UX
 * @graph-business trigger の bounding rect から popover の fixed 座標を計算し、画面下端で収まらない場合は上へ反転・内部スクロール用の最大高さを返す。scroll/resize で再計算する
 * @graph-connects none
 */
export function useFloatingPosition(
  triggerRef: RefObject<HTMLElement | null>,
  open: boolean,
  floatingRef?: RefObject<HTMLElement | null>,
): FloatingRect | null {
  const [rect, setRect] = useState<FloatingRect | null>(null);
  useLayoutEffect(() => {
    if (!open) {
      setRect(null);
      return;
    }
    const update = (): void => {
      const trigger = triggerRef.current;
      if (trigger === null) return;
      const r = trigger.getBoundingClientRect();

      // popover は open と同時に (visibility: hidden で) mount されるため、この時点で実高さを
      // 測れる。未指定・未 mount の場合は 0 になり、常に下へ置く従来の挙動に落ちる。
      const height = floatingRef?.current?.offsetHeight ?? 0;
      const spaceBelow = window.innerHeight - r.bottom - GAP - VIEWPORT_MARGIN;
      const spaceAbove = r.top - GAP - VIEWPORT_MARGIN;

      // 下に収まるならそのまま下。収まらない時だけ、より広い側へ寄せる。
      const placement: FloatingRect['placement'] =
        height <= spaceBelow || spaceBelow >= spaceAbove ? 'bottom' : 'top';
      const maxHeight = Math.max(placement === 'bottom' ? spaceBelow : spaceAbove, MIN_HEIGHT);
      const top =
        placement === 'bottom'
          ? r.bottom + GAP
          : Math.max(VIEWPORT_MARGIN, r.top - GAP - Math.min(height, maxHeight));

      setRect({ top, left: r.left, width: r.width, placement, maxHeight });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, triggerRef, floatingRef]);
  return rect;
}
